import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../contexts/NotificationContext';
import './styles.css';

export default function CreateFamily() {
  const [familyName, setFamilyName] = useState('');
  const [name, setName] = useState('');
  const [adminPasscode, setAdminPasscode] = useState('');
  const [confirmAdminPasscode, setConfirmAdminPasscode] = useState('');
  const [privacyFolderPasscode, setPrivacyFolderPasscode] = useState('');
  const [confirmPrivacyFolderPasscode, setConfirmPrivacyFolderPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingFamily, setCheckingFamily] = useState(true);
  const [hasExistingFamily, setHasExistingFamily] = useState(false);
  const [existingFamilyName, setExistingFamilyName] = useState('');
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showVaultSeal, setShowVaultSeal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const { showSuccess, showError, showInfo } = useNotification();
  const queryClient = useQueryClient();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

  // Check if user already has a family
  useEffect(() => {
    const checkExistingFamily = async () => {
      if (!isAuthenticated) {
        setCheckingFamily(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${API_BASE_URL}/api/families/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.exists) {
            setHasExistingFamily(true);
            setExistingFamilyName(data.familyName || 'your family');
            showInfo(`Your family vault (${data.familyName || 'your family'}) is already established. Redirecting to your vault...`);
            setTimeout(() => navigate('/member-selection'), 2000);
          }
        }
      } catch (error) {
        console.error('Error checking existing family:', error);
      } finally {
        setCheckingFamily(false);
      }
    };

    checkExistingFamily();
  }, [isAuthenticated, getAccessTokenSilently, navigate, showInfo]);

  // Handle payment success notification
  useEffect(() => {
    if (location.state?.paymentSuccess) {
      setShowPaymentSuccess(true);
      // Auto-hide after 5 seconds
      setTimeout(() => setShowPaymentSuccess(false), 5000);
    }
  }, [location.state]);

  // Redirect to login if not authenticated (after all hooks)
  if (!isAuthenticated) {
    return (
      <div className="create-family-bg">
        <div className="create-family-container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h1 style={{ color: '#e5c97b', marginBottom: '20px' }}>Authentication Required</h1>
            <p style={{ fontSize: '16px', color: '#e2c89c', marginBottom: '20px' }}>
              Please log in to create your family vault.
            </p>
            <button
              onClick={() => loginWithRedirect({
                appState: { returnTo: '/create-family' }
              })}
              style={{
                background: 'linear-gradient(90deg, #e5c97b 0%, #bfa97a 100%)',
                color: '#18120b',
                padding: '1rem 2rem',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Cinzel, serif',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Log In
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!familyName) {
      setError('Please enter a Family Line Name.');
      return;
    }
    if (!name) {
      setError('Please enter the Founding Custodian name.');
      return;
    }
    if (!adminPasscode || !confirmAdminPasscode) {
      setError('Please enter and confirm the Founder\'s Vault Key.');
      return;
    }
    if (adminPasscode.length !== 4 || !/^\d+$/.test(adminPasscode)) {
      setError('Founder\'s Vault Key must be exactly 4 digits.');
      return;
    }
    if (adminPasscode !== confirmAdminPasscode) {
      setError('Vault Keys do not match.');
      return;
    }
    if (!privacyFolderPasscode || !confirmPrivacyFolderPasscode) {
      setError('Please enter and confirm the Private Archive Key.');
      return;
    }
    if (privacyFolderPasscode.length !== 4 || !/^\d+$/.test(privacyFolderPasscode)) {
      setError('Private Archive Key must be exactly 4 digits.');
      return;
    }
    if (privacyFolderPasscode !== confirmPrivacyFolderPasscode) {
      setError('Archive Keys do not match.');
      return;
    }

    setLoading(true);

    try {
      const token = await getAccessTokenSilently({
        audience: "https://api.eternavaults.com"
      });

      const selectedPlan = localStorage.getItem('selectedPlan');

      const response = await fetch(`${API_BASE_URL}/api/families`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: familyName,
          plan: selectedPlan,
          passcode: adminPasscode,
          privacyFolderPasscode: privacyFolderPasscode,
          user: {
            name: name || 'ADMIN',
            auth0Id: user.sub,
            email: user.email
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'We couldn\'t establish your family vault. Please review the information and try again.');
      }

      const data = await response.json();
      
      // Wait a moment for database to fully commit the transaction
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check subscription status after creating family (with retry)
      let subscriptionActive = false;
      let retries = 3;
      
      while (retries > 0 && !subscriptionActive) {
        try {
          const subscriptionRes = await fetch(`${API_BASE_URL}/api/subscription/status`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store' // Prevent caching
          });
          
          if (subscriptionRes.ok) {
            const subscriptionData = await subscriptionRes.json();
            subscriptionActive = subscriptionData.active;
            
            if (subscriptionActive) {
              // User has active subscription, go to member selection
              showSuccess('Your family vault has been established. Welcome to EternaVaults.');
              
              // Invalidate all cached queries to ensure fresh data
              queryClient.invalidateQueries(['currentUser']);
              queryClient.invalidateQueries(['familyMembers']);
              queryClient.invalidateQueries(['storageUsage']);
              queryClient.invalidateQueries(['subscriptionStatus']);
              queryClient.invalidateQueries(['planInfo']);
              
              // Clear session storage that might interfere
              sessionStorage.removeItem('currentMember');
              sessionStorage.removeItem('passcodeVerifiedAt');
              
              // Show vault seal animation before navigating
              setShowVaultSeal(true);
              setTimeout(() => {
                setShowVaultSeal(false);
                navigate('/member-selection', { replace: true });
              }, 1600);
              return;
            }
          }
        } catch (subscriptionError) {
          console.error('Error checking subscription (attempts remaining:', retries, '):', subscriptionError);
        }
        
        retries--;
        if (retries > 0 && !subscriptionActive) {
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // If no active subscription, redirect to choose plan
      showSuccess('Your family vault has been established. Please complete your subscription to continue.');
      navigate('/choose-plan', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking for existing family
  if (checkingFamily) {
    return (
      <div className="create-family-bg">
        <div className="create-family-container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '18px', color: '#666' }}>Checking your account...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show message if user already has a family
  if (hasExistingFamily) {
    return (
      <div className="create-family-bg">
        <div className="create-family-container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h1 style={{ color: '#10b981', marginBottom: '20px' }}>✓ Your Family Vault is Established</h1>
            <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
              Your family vault{existingFamilyName ? ` (${existingFamilyName})` : ''} is already established. Redirecting you to your vault...
            </p>
            <div style={{ fontSize: '14px', color: '#999' }}>
              If you're not redirected automatically, <a href="/member-selection" style={{ color: '#3b82f6' }}>click here</a>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-family-bg">
      {/* Animated Background Particles */}
      <div className="particle particle--1"></div>
      <div className="particle particle--2"></div>
      <div className="particle particle--3"></div>
      <div className="particle particle--4"></div>
      <div className="particle particle--5"></div>
      <div className="particle particle--6"></div>
      
      {/* Vault Seal Success Animation */}
      {showVaultSeal && (
        <div className="vault-seal-overlay" aria-hidden="true">
          <div className="vault-seal">
            <div className="seal-ring seal-ring--outer"></div>
            <div className="seal-ring seal-ring--inner"></div>
            <div className="seal-core">✔</div>
          </div>
        </div>
      )}
      
      <div className="create-family-container">
        {/* Payment Success Notification */}
        {showPaymentSuccess && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: 'linear-gradient(135deg, #4CAF50, #45a049)',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            <div style={{ fontSize: '24px' }}>🎉</div>
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Payment Successful!</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Welcome to EternaVaults! Please create your family vault.</div>
            </div>
            <button
              onClick={() => setShowPaymentSuccess(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '4px',
                marginLeft: '8px'
              }}
            >
              ×
            </button>
          </div>
        )}
        
        <h1 className="vault-title">Establish Your Family Vault</h1>
        <p className="vault-subtitle">
          Your vault is the private cornerstone of your family's digital lineage.
        </p>

        <div className="vault-intro">
          <p>
            Names and passcodes you set here form the first layer of your vault's
            protection. Choose with intention — this structure supports
            memories, documents, and continuity for generations.
          </p>
        </div>

        <form className="simple-form luxury-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Family Line Name</label>
            <input
              type="text"
              value={familyName}
              onChange={e => setFamilyName(e.target.value)}
              disabled={loading}
              placeholder="e.g. The Harrington Lineage"
            />
          </div>
          
          <div className="form-group">
            <label>Founding Custodian</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={loading}
              placeholder="Your full name"
            />
          </div>
          
          <div className="form-group">
            <label>Founder's Vault Key (4 digits)</label>
            <input
              type="password"
              value={adminPasscode}
              onChange={e => setAdminPasscode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              disabled={loading}
              placeholder="Enter 4-digit key"
              autoComplete="new-password"
              maxLength={4}
              inputMode="numeric"
            />
          </div>
          
          <div className="form-group">
            <label>Confirm Vault Key</label>
            <input
              type="password"
              value={confirmAdminPasscode}
              onChange={e => setConfirmAdminPasscode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              disabled={loading}
              placeholder="Re-enter 4-digit key"
              autoComplete="new-password"
              maxLength={4}
              inputMode="numeric"
            />
          </div>
          
          <div className="form-group">
            <label>Private Archive Key (4 digits)</label>
            <input
              type="password"
              value={privacyFolderPasscode}
              onChange={e => setPrivacyFolderPasscode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              disabled={loading}
              placeholder="Enter 4-digit archive key"
              autoComplete="new-password"
              maxLength={4}
              inputMode="numeric"
            />
          </div>
          
          <div className="form-group">
            <label>Confirm Archive Key</label>
            <input
              type="password"
              value={confirmPrivacyFolderPasscode}
              onChange={e => setConfirmPrivacyFolderPasscode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              disabled={loading}
              placeholder="Re-enter 4-digit archive key"
              autoComplete="new-password"
              maxLength={4}
              inputMode="numeric"
            />
          </div>

          {error && <div className="form-error">{error}</div>}
          <button type="submit" disabled={loading}>
            {loading ? 'Establishing...' : 'Create Family Vault'}
          </button>
        </form>
      </div>
    </div>
  );
}
